/**
 * JobAutoFill — Jobber Custom Field Auto-Fill Engine UI
 *
 * 1. Search for a job by number or client name
 * 2. Preview the computed values with source breakdowns
 * 3. Edit any value inline before pushing
 * 4. Push to Jobber and see per-field results
 */
import { useState, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(n, dec = 1) {
  if (n == null) return "—";
  return (+n).toFixed(dec);
}

function fmtDate(d) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[+m - 1]} ${+day}, ${y}`;
}

function fmtCurrency(n) {
  if (n == null) return "—";
  return "$" + (+n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function buildPersonList(byPerson) {
  if (!byPerson || !Object.keys(byPerson).length) return "";
  return Object.entries(byPerson)
    .sort(([, a], [, b]) => b - a)
    .map(([name, hrs]) => {
      const first = name.split(" ")[0];
      return `${first} ${fmt(hrs)}h`;
    })
    .join(", ");
}

// ── API calls ──────────────────────────────────────────────────────────────────
async function searchJobInJobber(jobNumber) {
  const res = await apiFetch("/api/jobber", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ action: "findJob", jobNumber: String(jobNumber) }),
  });
  const data = await res.json();
  if (!res.ok || !data.job) throw new Error(data.error || "Job not found");
  return data.job; // { id, jobNumber, title, closedAt, client: { name } }
}

async function callAutofill(jobId, jobNumber, dryRun, overrides = {}, forceLabels = []) {
  const res = await apiFetch("/api/jobber-autofill", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({
      action: "autofillJob",
      jobId,
      jobNumber,
      dryRun,
      overrides,
      forceLabels,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Auto-fill request failed");
  return data;
}

// Mirror of the API's blank test so the UI can tell "will fill" from
// "protected — already has a value".
function cfIsBlank(v) {
  if (v == null) return true;
  const s = String(v).trim();
  if (s === "") return true;
  const n = parseFloat(s.replace(/[$,\s]/g, ""));
  return !isNaN(n) && n === 0 && /^[$\s0.,-]*$/.test(s);
}

// ── Sub-components ────────────────────────────────────────────────────────────
function JobCard({ job, onSelect, selected }) {
  return (
    <button
      onClick={() => onSelect(job)}
      style={{
        width: "100%", textAlign: "left", background: selected ? "rgba(165,206,58,.1)" : "#161a20",
        border: `1px solid ${selected ? "#a5ce3a" : "#2a3140"}`,
        borderRadius: 10, padding: "12px 16px", cursor: "pointer",
        color: "#e8eaf0", marginBottom: 8, display: "flex", alignItems: "center", gap: 12,
        transition: "border-color 0.15s",
      }}
    >
      <div style={{ background: "#1e2328", borderRadius: 8, padding: "4px 10px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: "#a5ce3a", minWidth: 60, textAlign: "center" }}>
        #{job.jobNumber}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{job.client?.name || "Unknown Client"}</div>
        {job.title && <div style={{ fontSize: 12, color: "#8892a4", marginTop: 2 }}>{job.title}</div>}
      </div>
      {job.closedAt && (
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#8892a4" }}>
          Closed {fmtDate(job.closedAt?.slice(0, 10))}
        </div>
      )}
    </button>
  );
}

function SourceBadge({ text }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: "#8892a4",
      background: "#1e2328", border: "1px solid #2a3140",
      borderRadius: 4, padding: "1px 5px", letterSpacing: ".03em",
      textTransform: "uppercase",
    }}>
      {text}
    </span>
  );
}

function PreviewRow({ label, display, detail, source, editable, editValue, onEdit, editType = "text", pushStatus, currentValue, overwrite, onToggleOverwrite }) {
  const statusColor = pushStatus === "ok" ? "#a5ce3a" : pushStatus === "error" ? "#ef4444" : "transparent";
  const hasCurrent = currentValue !== undefined && !cfIsBlank(currentValue);
  return (
    <div style={{
      borderBottom: "1px solid #1e2328", padding: "14px 0",
      display: "grid", gridTemplateColumns: "200px 1fr", gap: 16, alignItems: "start",
    }}>
      {/* Left: field name */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#e8eaf0", marginBottom: 4 }}>{label}</div>
        <SourceBadge text={source} />
        {pushStatus && (
          <div style={{ marginTop: 4, fontSize: 11, color: statusColor, fontWeight: 600 }}>
            {pushStatus === "ok" ? "✓ Pushed" : pushStatus === "error" ? "✗ Failed" : ""}
          </div>
        )}
      </div>

      {/* Right: value + breakdown + edit */}
      <div>
        {/* Editable value */}
        {editable ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            {editType === "select-yn" ? (
              <select
                value={editValue}
                onChange={e => onEdit(e.target.value)}
                style={{
                  background: "#1e2328", border: "1px solid #a5ce3a", borderRadius: 6,
                  color: "#e8eaf0", fontSize: 14, fontWeight: 600, padding: "4px 8px",
                  fontFamily: "'Roboto', sans-serif",
                }}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            ) : (
              <input
                type={editType}
                value={editValue}
                onChange={e => onEdit(e.target.value)}
                style={{
                  background: "#1e2328", border: "1px solid #a5ce3a", borderRadius: 6,
                  color: "#e8eaf0", fontSize: 14, fontWeight: 600, padding: "4px 8px",
                  fontFamily: "'Roboto', sans-serif", minWidth: 140,
                }}
              />
            )}
            <span style={{ fontSize: 11, color: "#8892a4" }}>editable</span>
          </div>
        ) : (
          <div style={{ fontSize: 15, fontWeight: 700, color: "#e8eaf0", marginBottom: 4 }}>
            {display}
          </div>
        )}

        {/* Breakdown detail */}
        {detail && (
          <div style={{ fontSize: 12, color: "#8892a4", lineHeight: 1.5 }}>{detail}</div>
        )}

        {/* Overwrite protection: field already has a value in Jobber */}
        {hasCurrent && (
          <div style={{
            marginTop: 6, display: "flex", alignItems: "center", gap: 8,
            background: "rgba(245,158,11,.07)", border: "1px solid rgba(245,158,11,.25)",
            borderRadius: 6, padding: "5px 10px", fontSize: 12,
          }}>
            <span style={{ color: "#f59e0b" }}>
              In Jobber now: <b>{String(currentValue)}</b> — protected
            </span>
            {onToggleOverwrite && (
              <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, cursor: "pointer", color: overwrite ? "#ef4444" : "#8892a4", fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={!!overwrite}
                  onChange={onToggleOverwrite}
                  style={{ accentColor: "#ef4444" }}
                />
                overwrite
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function JobAutoFill() {
  const [searchTerm,    setSearchTerm]    = useState("");
  const [searching,     setSearching]     = useState(false);
  const [searchError,   setSearchError]   = useState(null);
  const [foundJob,      setFoundJob]      = useState(null);  // Jobber job object

  const [previewing,    setPreviewing]    = useState(false);
  const [previewError,  setPreviewError]  = useState(null);
  const [preview,       setPreview]       = useState(null);  // API preview response

  const [edits,         setEdits]         = useState({});    // metric key → override value
  const [overwrites,    setOverwrites]    = useState({});    // Jobber CF label → force overwrite

  const [pushing,       setPushing]       = useState(false);
  const [pushResponse,  setPushResponse]  = useState(null);

  // ── Search ──────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;

    setSearching(true);
    setSearchError(null);
    setFoundJob(null);
    setPreview(null);
    setPushResponse(null);
    setEdits({});
    setOverwrites({});

    try {
      const job = await searchJobInJobber(term);
      setFoundJob(job);
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  // ── Preview ─────────────────────────────────────────────────────────────────
  const handlePreview = useCallback(async () => {
    if (!foundJob) return;
    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);
    setPushResponse(null);
    setEdits({});
    setOverwrites({});

    try {
      const data = await callAutofill(foundJob.id, foundJob.jobNumber, true);
      setPreview(data);
      // Initialise edits with computed values so the inputs are pre-filled
      const m = data.metrics || {};
      setEdits({
        vanHoursQuoted:       m.vanHoursQuoted      ?? "",
        hoursDollarQuoted:    m.hoursDollarQuoted   ?? "",
        expensesQuoted:       m.expensesQuoted      ?? "",
        quotedTotal:          m.quotedTotal         ?? "",
        vanHoursActual:       m.vanHoursActual      ?? "",
        leadTech:             m.leadTech            ?? "",
        workCompletedDate:    m.workCompletedDate    ?? "",
        jmanHours:            m.jmanHours            ?? "",
        apprenticeHours:      m.apprenticeHours      ?? "",
        supplierPurchaseCount: String(m.supplierPurchaseCount ?? ""),
        timesheetsAccurate:   m.timesheetsAccurate   ?? "No",
        closeOutOnTime:       m.closeOutOnTime        ?? "No",
        clientRequestedDate:  m.clientRequestedDate  ?? "",
      });
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewing(false);
    }
  }, [foundJob]);

  // ── Push ────────────────────────────────────────────────────────────────────
  const handlePush = useCallback(async () => {
    if (!foundJob || !preview) return;
    setPushing(true);

    // Build overrides from edited values (only changed ones)
    const overrides = {};
    const m = preview.metrics || {};
    if (edits.vanHoursQuoted    !== String(m.vanHoursQuoted    ?? "")) overrides.vanHoursQuoted    = parseFloat(edits.vanHoursQuoted) || 0;
    if (edits.hoursDollarQuoted !== String(m.hoursDollarQuoted ?? "")) overrides.hoursDollarQuoted = parseFloat(edits.hoursDollarQuoted) || 0;
    if (edits.expensesQuoted    !== String(m.expensesQuoted    ?? "")) overrides.expensesQuoted    = parseFloat(edits.expensesQuoted) || 0;
    if (edits.quotedTotal       !== String(m.quotedTotal       ?? "")) overrides.quotedTotal       = parseFloat(edits.quotedTotal) || 0;
    if (edits.vanHoursActual    !== String(m.vanHoursActual    ?? "")) overrides.vanHoursActual    = parseFloat(edits.vanHoursActual) || 0;
    if (edits.leadTech            !== (m.leadTech            ?? ""))  overrides.leadTech            = edits.leadTech;
    if (edits.workCompletedDate   !== (m.workCompletedDate   ?? ""))  overrides.workCompletedDate   = edits.workCompletedDate;
    if (edits.jmanHours           !== String(m.jmanHours     ?? ""))  overrides.jmanHours           = parseFloat(edits.jmanHours) || 0;
    if (edits.apprenticeHours     !== String(m.apprenticeHours ?? "")) overrides.apprenticeHours    = parseFloat(edits.apprenticeHours) || 0;
    if (edits.supplierPurchaseCount !== String(m.supplierPurchaseCount ?? "")) overrides.supplierPurchaseCount = parseInt(edits.supplierPurchaseCount) || 0;
    if (edits.timesheetsAccurate  !== (m.timesheetsAccurate  ?? "No")) overrides.timesheetsAccurate = edits.timesheetsAccurate;
    if (edits.closeOutOnTime      !== (m.closeOutOnTime       ?? "No")) overrides.closeOutOnTime     = edits.closeOutOnTime;
    if (edits.clientRequestedDate !== (m.clientRequestedDate ?? ""))   overrides.clientRequestedDate = edits.clientRequestedDate;

    const forceLabels = Object.keys(overwrites).filter(l => overwrites[l]);

    try {
      const data = await callAutofill(foundJob.id, foundJob.jobNumber, false, overrides, forceLabels);
      setPushResponse(data);
    } catch (err) {
      setPushResponse({ ok: false, error: err.message });
    } finally {
      setPushing(false);
    }
  }, [foundJob, preview, edits, overwrites]);

  // ── Render ──────────────────────────────────────────────────────────────────
  const m = preview?.metrics || {};
  const s = preview?.sources || {};

  // Current Jobber value for a CF label (case-insensitive)
  const cur = (label) => {
    const cv = preview?.currentValues || {};
    if (label in cv) return cv[label];
    const lower = label.toLowerCase().trim();
    const k = Object.keys(cv).find(x => x.toLowerCase().trim() === lower);
    return k !== undefined ? cv[k] : undefined;
  };
  // Toggle overwrite for one or more labels at once (dual-CF rows)
  const toggleOw = (...labels) => () =>
    setOverwrites(p => {
      const next = { ...p };
      const on = !labels.every(l => p[l]);
      labels.forEach(l => { next[l] = on; });
      return next;
    });

  // Build per-field push status map
  const pushStatusMap = {};
  if (pushResponse?.fieldResults) {
    for (const fr of pushResponse.fieldResults) {
      pushStatusMap[fr.label] = fr.ok ? "ok" : "error";
    }
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 16px", color: "#e8eaf0", fontFamily: "'Roboto', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: ".04em", color: "#a5ce3a", marginBottom: 4 }}>
          Job Auto-Fill
        </div>
        <div style={{ fontSize: 14, color: "#8892a4" }}>
          Pulls quoted values from the Log Book + crew actuals from Supabase, pushes both to Jobber custom fields
        </div>
      </div>

      {/* ── Search Box ── */}
      <form onSubmit={handleSearch} style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            placeholder="Job number (e.g. 4521)"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              flex: 1, background: "#161a20", border: "1px solid #2a3140",
              borderRadius: 10, color: "#e8eaf0", fontSize: 15, padding: "10px 14px",
              fontFamily: "'Roboto', sans-serif", outline: "none",
            }}
            onFocus={e => e.target.style.borderColor = "#a5ce3a"}
            onBlur={e  => e.target.style.borderColor = "#2a3140"}
          />
          <button
            type="submit"
            disabled={searching || !searchTerm.trim()}
            style={{
              background: "#a5ce3a", color: "#0d0f12", border: "none",
              borderRadius: 10, padding: "10px 20px", fontWeight: 700,
              fontSize: 14, cursor: "pointer", fontFamily: "'Roboto', sans-serif",
              opacity: searching || !searchTerm.trim() ? 0.5 : 1,
            }}
          >
            {searching ? "Searching…" : "Find Job"}
          </button>
        </div>
      </form>

      {searchError && (
        <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, padding: 14, marginBottom: 16, color: "#ef4444", fontSize: 14 }}>
          {searchError}
        </div>
      )}

      {/* ── Found Job Card ── */}
      {foundJob && (
        <div style={{ marginBottom: 20 }}>
          <JobCard job={foundJob} onSelect={() => {}} selected />
          <button
            onClick={handlePreview}
            disabled={previewing}
            style={{
              width: "100%", background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.4)",
              borderRadius: 10, color: "#a5ce3a", fontWeight: 700, fontSize: 15,
              padding: "12px 0", cursor: "pointer", fontFamily: "'Roboto', sans-serif",
              marginTop: 10, opacity: previewing ? 0.6 : 1,
            }}
          >
            {previewing ? "Calculating…" : "Preview Auto-Fill Values"}
          </button>
        </div>
      )}

      {previewError && (
        <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, padding: 14, marginBottom: 16, color: "#ef4444", fontSize: 14 }}>
          Preview error: {previewError}
        </div>
      )}

      {/* ── Preview Panel ── */}
      {preview && (
        <div style={{ background: "#161a20", border: "1px solid #2a3140", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>

          {/* Sources summary */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #2a3140" }}>
            <SrcChip label="Timesheets" value={s.timesheets} />
            <SrcChip label="Visits"     value={`${s.completedVisits}/${s.visits} completed`} />
            <SrcChip label="Notes"      value={s.notes} />
            <SrcChip label="Invoices"   value={s.invoices} />
            <SrcChip label="jb_jobs"    value={s.jbJobFound ? "found" : "not found"} />
            <SrcChip label="Quote"      value={s.quoteFound ? `#${preview.quoted?.quoteNum} found` : "not found"} />
            <SrcChip label="CF matched" value={`${s.customFieldsMatched}/${s.customFieldsAvailable}`} />
          </div>

          {s.customFieldsMatched === 0 && (
            <div style={{ background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#ef4444", fontSize: 13 }}>
              No custom fields matched. The job may not have the expected custom fields configured in Jobber.
            </div>
          )}

          {/* ── Quoted section (from Log Book) ── */}
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, letterSpacing: ".05em", color: "#a5ce3a", marginBottom: 4 }}>
            Quoted — What We Bid
          </div>
          {preview.quoted?.available ? (
            <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 8 }}>
              Quote #{preview.quoted.quoteNum}
              {preview.quoted.quoteStatus ? ` · ${preview.quoted.quoteStatus}` : ""}
              {preview.quoted.tab ? ` · Log Book tab "${preview.quoted.tab}"` : ""}
              {` · ${preview.quoted.labourRows} labour + ${preview.quoted.expenseRows} expense rows`}
            </div>
          ) : (
            <div style={{ background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, color: "#f59e0b", fontSize: 13 }}>
              Quoted values unavailable: {preview.quoted?.reason || "unknown"} — you can still type them in manually below.
            </div>
          )}
          {(preview.quoted?.warnings || []).map((w, i) => (
            <div key={i} style={{ background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 8, padding: "8px 14px", marginBottom: 8, color: "#f59e0b", fontSize: 12 }}>
              ⚠ {w}
            </div>
          ))}

          <PreviewRow
            label="Van Hours QTY Quoted"
            display={m.vanHoursQuoted != null ? `${fmt(m.vanHoursQuoted)}h` : "—"}
            detail={preview.quoted?.available ? `Sum of "h" rows for quote #${preview.quoted.quoteNum}` : null}
            source="Log Book"
            editable
            editType="number"
            editValue={edits.vanHoursQuoted ?? ""}
            onEdit={v => setEdits(p => ({ ...p, vanHoursQuoted: v }))}
            pushStatus={pushStatusMap["Van Hours QTY Quoted"]}
            currentValue={cur("Van Hours QTY Quoted")}
            overwrite={overwrites["Van Hours QTY Quoted"]}
            onToggleOverwrite={toggleOw("Van Hours QTY Quoted")}
          />

          <PreviewRow
            label="Hours $ Quoted"
            display={fmtCurrency(m.hoursDollarQuoted)}
            detail={preview.quoted?.available ? "Client sell price of quoted labour rows" : null}
            source="Log Book"
            editable
            editType="number"
            editValue={edits.hoursDollarQuoted ?? ""}
            onEdit={v => setEdits(p => ({ ...p, hoursDollarQuoted: v }))}
            pushStatus={pushStatusMap["Hours $ Quoted"]}
            currentValue={cur("Hours $ Quoted")}
            overwrite={overwrites["Hours $ Quoted"]}
            onToggleOverwrite={toggleOw("Hours $ Quoted")}
          />

          <PreviewRow
            label="Expenses Quoted (Mat/Sub/Permit)"
            display={fmtCurrency(m.expensesQuoted)}
            detail={preview.quoted?.available
              ? `Our cost incl PST — compares to supplier invoices. Sell side: ${fmtCurrency(preview.quoted.expensesSellQuoted)}`
              : null}
            source="Log Book"
            editable
            editType="number"
            editValue={edits.expensesQuoted ?? ""}
            onEdit={v => setEdits(p => ({ ...p, expensesQuoted: v }))}
            pushStatus={pushStatusMap["Expenses: Material/Sub/permit etc. $ Quoted"]}
            currentValue={cur("Expenses: Material/Sub/permit etc. $ Quoted")}
            overwrite={overwrites["Expenses: Material/Sub/permit etc. $ Quoted"]}
            onToggleOverwrite={toggleOw("Expenses: Material/Sub/permit etc. $ Quoted")}
          />

          <PreviewRow
            label="Quoted Total"
            display={fmtCurrency(m.quotedTotal)}
            detail={
              preview.quoted?.quoteSubtotal != null
                ? `Jobber quote subtotal (pre-GST). Log Book sell sum: ${fmtCurrency(preview.quoted.logbookTotalSell)}`
                : (preview.quoted?.available ? "Log Book sell sum (pre-GST)" : null)
            }
            source={preview.quoted?.quoteSubtotal != null ? "jb_quotes" : "Log Book"}
            editable
            editType="number"
            editValue={edits.quotedTotal ?? ""}
            onEdit={v => setEdits(p => ({ ...p, quotedTotal: v }))}
            pushStatus={pushStatusMap["Quoted Total"]}
            currentValue={cur("Quoted Total")}
            overwrite={overwrites["Quoted Total"]}
            onToggleOverwrite={toggleOw("Quoted Total")}
          />

          {/* ── Actuals section ── */}
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, letterSpacing: ".05em", color: "#a5ce3a", margin: "20px 0 4px" }}>
            Actuals — Where We Landed
          </div>

          <PreviewRow
            label="Lead Tech"
            display={m.leadTech || "—"}
            detail={m.leadTech ? `${m.leadTech} — ${fmt(m.leadTechHours)}h on this job` : "No timesheets found"}
            source="jobber_timesheets"
            editable
            editType="text"
            editValue={edits.leadTech ?? ""}
            onEdit={v => setEdits(p => ({ ...p, leadTech: v }))}
            pushStatus={pushStatusMap["Lead Tech"]}
            currentValue={cur("Lead Tech")}
            overwrite={overwrites["Lead Tech"]}
            onToggleOverwrite={toggleOw("Lead Tech")}
          />

          <PreviewRow
            label="Work Completed Date"
            display={fmtDate(m.workCompletedDate)}
            detail={m.lastVisitDate ? `Last completed visit: ${fmtDate(m.lastVisitDate)} (${s.completedVisits} visits total)` : "No completed visits"}
            source="jobber_visits"
            editable
            editType="date"
            editValue={edits.workCompletedDate ?? ""}
            onEdit={v => setEdits(p => ({ ...p, workCompletedDate: v }))}
            pushStatus={pushStatusMap["Work Completed Date"]}
            currentValue={cur("Work Completed Date")}
            overwrite={overwrites["Work Completed Date"]}
            onToggleOverwrite={toggleOw("Work Completed Date")}
          />

          <PreviewRow
            label="Van Hours Used"
            display={m.vanHoursActual != null ? `${fmt(m.vanHoursActual)}h` : "—"}
            detail={`Total man-hours ${fmt((m.jmanHours || 0) + (m.apprenticeHours || 0))}h ÷ 2 (two techs per van)`}
            source="jobber_timesheets"
            editable
            editType="number"
            editValue={edits.vanHoursActual ?? ""}
            onEdit={v => setEdits(p => ({ ...p, vanHoursActual: v }))}
            pushStatus={pushStatusMap["Van Hours used on job"] ?? pushStatusMap["Van Hours Used"]}
            currentValue={cur("Van Hours used on job") ?? cur("Van Hours Used")}
            overwrite={overwrites["Van Hours used on job"]}
            onToggleOverwrite={toggleOw("Van Hours used on job", "Van Hours Used")}
          />

          <PreviewRow
            label="Jman Hours"
            display={`${fmt(m.jmanHours)}h`}
            detail={buildPersonList(m.jmanByPerson) || "No journeyman timesheets"}
            source="jobber_timesheets"
            editable
            editType="number"
            editValue={edits.jmanHours ?? ""}
            onEdit={v => setEdits(p => ({ ...p, jmanHours: v }))}
            pushStatus={pushStatusMap["Jman Hours used on job"]}
            currentValue={cur("Jman Hours used on job")}
            overwrite={overwrites["Jman Hours used on job"]}
            onToggleOverwrite={toggleOw("Jman Hours used on job")}
          />

          <PreviewRow
            label="Apprentice Hours"
            display={`${fmt(m.apprenticeHours)}h`}
            detail={buildPersonList(m.apprenticeByPerson) || "No apprentice timesheets"}
            source="jobber_timesheets"
            editable
            editType="number"
            editValue={edits.apprenticeHours ?? ""}
            onEdit={v => setEdits(p => ({ ...p, apprenticeHours: v }))}
            pushStatus={pushStatusMap["Apprentice Hours used on job"]}
            currentValue={cur("Apprentice Hours used on job")}
            overwrite={overwrites["Apprentice Hours used on job"]}
            onToggleOverwrite={toggleOw("Apprentice Hours used on job")}
          />

          <PreviewRow
            label="# Supplier Purchases"
            display={m.supplierPurchaseCount != null ? `${m.supplierPurchaseCount} invoice${m.supplierPurchaseCount !== 1 ? "s" : ""}` : "—"}
            detail={m.supplierTotal > 0 ? `Total: ${fmtCurrency(m.supplierTotal)}` : "No invoices in pending_invoices"}
            source="pending_invoices"
            editable
            editType="number"
            editValue={edits.supplierPurchaseCount ?? ""}
            onEdit={v => setEdits(p => ({ ...p, supplierPurchaseCount: v }))}
            pushStatus={pushStatusMap["# of supplier Purchases"]}
            currentValue={cur("# of supplier Purchases")}
            overwrite={overwrites["# of supplier Purchases"]}
            onToggleOverwrite={toggleOw("# of supplier Purchases")}
          />

          <PreviewRow
            label="Timesheets Accurate"
            display={m.timesheetsAccurate || "—"}
            detail={m.timesheetsDetail || ""}
            source="jobber_timesheets"
            editable
            editType="select-yn"
            editValue={edits.timesheetsAccurate ?? "No"}
            onEdit={v => setEdits(p => ({ ...p, timesheetsAccurate: v }))}
            pushStatus={pushStatusMap["Employee timesheets accurate"] ?? pushStatusMap["Employee time sheets not accurate/missing/late"]}
            currentValue={cur("Employee timesheets accurate") ?? cur("Employee time sheets not accurate/missing/late")}
            overwrite={overwrites["Employee timesheets accurate"]}
            onToggleOverwrite={toggleOw("Employee timesheets accurate", "Employee time sheets not accurate/missing/late")}
          />

          <PreviewRow
            label="Job Close Out on Time"
            display={m.closeOutOnTime || "—"}
            detail={
              m.closeDate && m.lastVisitDate
                ? `Job closed: ${fmtDate(m.closeDate)} — Last visit: ${fmtDate(m.lastVisitDate)}`
                : m.closeDate ? `Closed ${fmtDate(m.closeDate)} — no completed visits` : "Job not yet closed in Jobber"
            }
            source="Jobber closedAt"
            editable
            editType="select-yn"
            editValue={edits.closeOutOnTime ?? "No"}
            onEdit={v => setEdits(p => ({ ...p, closeOutOnTime: v }))}
            pushStatus={pushStatusMap["Job Close Out on time"] ?? pushStatusMap["Job Close Out + Timesheets + Notes Updated on time?"]}
            currentValue={cur("Job Close Out on time") ?? cur("Job Close Out + Timesheets + Notes Updated on time?")}
            overwrite={overwrites["Job Close Out on time"]}
            onToggleOverwrite={toggleOw("Job Close Out on time", "Job Close Out + Timesheets + Notes Updated on time?")}
          />

          <PreviewRow
            label="Client Requested Date"
            display={fmtDate(m.clientRequestedDate)}
            detail={m.clientRequestedDate ? `From jb_jobs.created_at` : "jb_jobs record not found for this job"}
            source="jb_jobs"
            editable
            editType="date"
            editValue={edits.clientRequestedDate ?? ""}
            onEdit={v => setEdits(p => ({ ...p, clientRequestedDate: v }))}
            pushStatus={pushStatusMap["Client Requested Date"]}
            currentValue={cur("Client Requested Date")}
            overwrite={overwrites["Client Requested Date"]}
            onToggleOverwrite={toggleOw("Client Requested Date")}
          />

          {/* Push button + result */}
          {!pushResponse && (
            <div style={{ marginTop: 24 }}>
              {(preview.skippedExisting?.length > 0) && (
                <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 10, textAlign: "center" }}>
                  {preview.skippedExisting.length} field{preview.skippedExisting.length !== 1 ? "s" : ""} already filled in Jobber will be left untouched — tick "overwrite" on a field to replace it
                </div>
              )}
              <button
                onClick={handlePush}
                disabled={pushing || s.customFieldsMatched === 0}
                style={{
                  width: "100%", background: "#a5ce3a", color: "#0d0f12",
                  border: "none", borderRadius: 10, fontWeight: 700, fontSize: 16,
                  padding: "14px 0", cursor: "pointer", fontFamily: "'Roboto', sans-serif",
                  opacity: pushing || s.customFieldsMatched === 0 ? 0.5 : 1,
                }}
              >
                {pushing ? "Pushing to Jobber…" : `Push ${s.customFieldsMatched} Field${s.customFieldsMatched !== 1 ? "s" : ""} to Jobber`}
              </button>
              {s.customFieldsMatched === 0 && (
                <div style={{ textAlign: "center", fontSize: 12, color: "#8892a4", marginTop: 8 }}>
                  No custom fields matched — nothing to push
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Push Result ── */}
      {pushResponse && (
        <div style={{
          background: pushResponse.ok ? "rgba(165,206,58,.08)" : "rgba(239,68,68,.08)",
          border: `1px solid ${pushResponse.ok ? "rgba(165,206,58,.3)" : "rgba(239,68,68,.3)"}`,
          borderRadius: 14, padding: "20px 24px", marginBottom: 20,
        }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: ".04em", color: pushResponse.ok ? "#a5ce3a" : "#ef4444", marginBottom: 12 }}>
            {pushResponse.ok ? "✓ Pushed Successfully" : "✗ Push Failed"}
          </div>

          {pushResponse.ok && pushResponse.push && (
            <div style={{ fontSize: 14, color: "#8892a4", marginBottom: 12 }}>
              {pushResponse.push.updatedCount} custom field{pushResponse.push.updatedCount !== 1 ? "s" : ""} updated in Jobber
            </div>
          )}

          {!pushResponse.ok && pushResponse.push?.errors && (
            <div style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>
              {pushResponse.push.errors.map((e, i) => <div key={i}>{e.message}</div>)}
            </div>
          )}

          {pushResponse.fieldResults?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pushResponse.fieldResults.map((fr, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span style={{ color: fr.ok ? "#a5ce3a" : "#ef4444", fontWeight: 700 }}>
                    {fr.ok ? "✓" : "✗"}
                  </span>
                  <span style={{ color: "#e8eaf0" }}>{fr.label}</span>
                  {fr.error && <span style={{ color: "#ef4444" }}>— {fr.error}</span>}
                </div>
              ))}
            </div>
          )}

          {pushResponse.skippedExisting?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", marginBottom: 6 }}>
                Left untouched (already filled in Jobber):
              </div>
              {pushResponse.skippedExisting.map((sk, i) => (
                <div key={i} style={{ fontSize: 12, color: "#8892a4", display: "flex", gap: 8 }}>
                  <span style={{ color: "#f59e0b" }}>⏸</span>
                  <span>{sk.label}</span>
                  <span style={{ color: "#e8eaf0" }}>= {String(sk.current)}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button
              onClick={() => { setPushResponse(null); handlePreview(); }}
              style={{
                background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.4)",
                borderRadius: 8, color: "#a5ce3a", fontWeight: 600, fontSize: 13,
                padding: "8px 16px", cursor: "pointer", fontFamily: "'Roboto', sans-serif",
              }}
            >
              Preview Again
            </button>
            <button
              onClick={() => {
                setFoundJob(null); setPreview(null); setPushResponse(null);
                setSearchTerm(""); setEdits({}); setOverwrites({});
              }}
              style={{
                background: "none", border: "1px solid #2a3140",
                borderRadius: 8, color: "#8892a4", fontWeight: 600, fontSize: 13,
                padding: "8px 16px", cursor: "pointer", fontFamily: "'Roboto', sans-serif",
              }}
            >
              New Job
            </button>
          </div>
        </div>
      )}

      {/* ── Backfill review for closed jobs ── */}
      <BackfillPanel />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;600;700&display=swap');
        input[type=number]::-webkit-inner-spin-button { opacity: 0.4; }
      `}</style>
    </div>
  );
}

// ── Backfill Review — closed jobs × Log Book, bulk push with protection ──────
const FLAG_STYLE = {
  CLEAN:          { color: "#a5ce3a", bg: "rgba(165,206,58,.12)" },
  NO_QUOTE:       { color: "#8892a4", bg: "#1e2328" },
  NO_LOGBOOK:     { color: "#f59e0b", bg: "rgba(245,158,11,.1)" },
  MULTI_PACKAGE:  { color: "#f59e0b", bg: "rgba(245,158,11,.1)" },
  ALREADY_FILLED: { color: "#3b82f6", bg: "rgba(59,130,246,.1)" },
};

function BackfillPanel() {
  const [open,      setOpen]      = useState(false);
  const [scanning,  setScanning]  = useState(false);
  const [scan,      setScan]      = useState(null);
  const [scanError, setScanError] = useState(null);
  const [selected,  setSelected]  = useState({});   // jobNum → true
  const [pushing,   setPushing]   = useState(false);
  const [progress,  setProgress]  = useState(null); // { done, total }
  const [results,   setResults]   = useState({});   // jobNum → { pushed, skipped, error }

  const runScan = async () => {
    setScanning(true);
    setScanError(null);
    setScan(null);
    setResults({});
    try {
      const res = await apiFetch("/api/jobber-autofill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "backfillScan" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Scan failed");
      setScan(data);
      // Pre-select the rows that are safe to push blind
      const sel = {};
      for (const r of data.rows) if (r.flags.includes("CLEAN")) sel[r.jobNum] = true;
      setSelected(sel);
    } catch (e) {
      setScanError(e.message);
    } finally {
      setScanning(false);
    }
  };

  const pushSelected = async () => {
    if (!scan) return;
    const targets = scan.rows.filter(r => selected[r.jobNum] && r.jobberId);
    setPushing(true);
    setProgress({ done: 0, total: targets.length });
    const out = {};
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      try {
        const res = await apiFetch("/api/jobber-autofill", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "autofillJob",
            jobId: r.jobberId,
            jobNumber: r.jobNum,
            // blanks-only (default) — never clobbers manual history
          }),
        });
        const data = await res.json();
        out[r.jobNum] = res.ok
          ? { pushed: data?.push?.updatedCount ?? 0, skipped: data?.skippedExisting?.length ?? 0 }
          : { error: data?.error || `HTTP ${res.status}` };
      } catch (e) {
        out[r.jobNum] = { error: e.message };
      }
      setResults({ ...out });
      setProgress({ done: i + 1, total: targets.length });
    }
    setPushing(false);
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div style={{ marginTop: 36, borderTop: "1px solid #2a3140", paddingTop: 20 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "none", cursor: "pointer", padding: 0,
          fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: ".04em",
          color: "#a5ce3a", display: "flex", alignItems: "center", gap: 10,
        }}
      >
        <span style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s", fontSize: 14 }}>▶</span>
        Backfill Review — Closed Jobs
      </button>
      <div style={{ fontSize: 13, color: "#8892a4", margin: "4px 0 12px 24px" }}>
        Scan every closed job, see what the Log Book says we quoted, bulk-push the clean ones.
        Already-filled fields are never overwritten here.
      </div>

      {open && (
        <div style={{ background: "#161a20", border: "1px solid #2a3140", borderRadius: 14, padding: 18 }}>
          {!scan && (
            <button
              onClick={runScan}
              disabled={scanning}
              style={{
                background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.4)",
                borderRadius: 10, color: "#a5ce3a", fontWeight: 700, fontSize: 14,
                padding: "10px 22px", cursor: "pointer", fontFamily: "'Roboto', sans-serif",
                opacity: scanning ? 0.6 : 1,
              }}
            >
              {scanning ? "Scanning closed jobs + Log Book…" : "Scan Closed Jobs"}
            </button>
          )}

          {scanError && (
            <div style={{ color: "#ef4444", fontSize: 13, marginTop: 10 }}>{scanError}</div>
          )}

          {scan && (
            <>
              {/* Summary chips */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <SrcChip label="Closed jobs" value={scan.totalClosedJobs} />
                {Object.entries(scan.counts || {}).map(([f, n]) => (
                  <SrcChip key={f} label={f.replace(/_/g, " ")} value={n} />
                ))}
              </div>
              {scan.logbookError && (
                <div style={{ background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 8, padding: "8px 14px", marginBottom: 12, color: "#f59e0b", fontSize: 12 }}>
                  Log Book unavailable: {scan.logbookError} — quoted columns will be blank.
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                <button
                  onClick={pushSelected}
                  disabled={pushing || selCount === 0}
                  style={{
                    background: "#a5ce3a", color: "#0d0f12", border: "none", borderRadius: 8,
                    fontWeight: 700, fontSize: 13, padding: "8px 18px", cursor: "pointer",
                    opacity: pushing || selCount === 0 ? 0.5 : 1, fontFamily: "'Roboto', sans-serif",
                  }}
                >
                  {pushing && progress
                    ? `Pushing ${progress.done}/${progress.total}…`
                    : `Push ${selCount} Selected (blanks only)`}
                </button>
                <button
                  onClick={runScan}
                  disabled={scanning || pushing}
                  style={{
                    background: "none", border: "1px solid #2a3140", borderRadius: 8,
                    color: "#8892a4", fontWeight: 600, fontSize: 13, padding: "8px 14px",
                    cursor: "pointer", fontFamily: "'Roboto', sans-serif",
                  }}
                >
                  Re-scan
                </button>
                <span style={{ fontSize: 12, color: "#8892a4" }}>
                  CLEAN rows pre-selected — tick others after eyeballing them
                </span>
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ color: "#8892a4", textAlign: "left" }}>
                      {["", "Job", "Client", "Closed", "Quote", "Van Hrs", "Hours $", "Expenses $", "Total $", "Flags", "Result"].map((h, i) => (
                        <th key={i} style={{ padding: "6px 8px", borderBottom: "1px solid #2a3140", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {scan.rows.map(r => {
                      const res = results[r.jobNum];
                      return (
                        <tr key={r.jobNum} style={{ borderBottom: "1px solid #1e2328", color: "#e8eaf0" }}>
                          <td style={{ padding: "5px 8px" }}>
                            <input
                              type="checkbox"
                              checked={!!selected[r.jobNum]}
                              onChange={() => setSelected(p => ({ ...p, [r.jobNum]: !p[r.jobNum] }))}
                              style={{ accentColor: "#a5ce3a" }}
                            />
                          </td>
                          <td style={{ padding: "5px 8px", color: "#a5ce3a", fontWeight: 700, whiteSpace: "nowrap" }}>#{r.jobNum}</td>
                          <td style={{ padding: "5px 8px", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.title || ""}>{r.client || "—"}</td>
                          <td style={{ padding: "5px 8px", color: "#8892a4", whiteSpace: "nowrap" }}>{r.closedDate || "—"}</td>
                          <td style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>{r.quoteNum ? `#${r.quoteNum}` : "—"}</td>
                          <td style={{ padding: "5px 8px" }}>{r.vanHoursQuoted ?? "—"}</td>
                          <td style={{ padding: "5px 8px" }}>{r.hoursDollarQuoted != null ? fmtCurrency(r.hoursDollarQuoted) : "—"}</td>
                          <td style={{ padding: "5px 8px" }}>{r.expensesQuoted != null ? fmtCurrency(r.expensesQuoted) : "—"}</td>
                          <td style={{ padding: "5px 8px" }}>{r.quotedTotal != null ? fmtCurrency(r.quotedTotal) : "—"}</td>
                          <td style={{ padding: "5px 8px" }}>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {r.flags.map(f => {
                                const st = FLAG_STYLE[f] || FLAG_STYLE.NO_QUOTE;
                                return (
                                  <span key={f} style={{ fontSize: 10, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap" }}>
                                    {f.replace(/_/g, " ")}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>
                            {res?.error && <span style={{ color: "#ef4444" }}>✗ {res.error.slice(0, 40)}</span>}
                            {res && !res.error && (
                              <span style={{ color: "#a5ce3a" }}>
                                ✓ {res.pushed} pushed{res.skipped ? `, ${res.skipped} protected` : ""}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Helper chip ───────────────────────────────────────────────────────────────
function SrcChip({ label, value }) {
  return (
    <div style={{
      background: "#1e2328", border: "1px solid #2a3140", borderRadius: 8,
      padding: "4px 10px", fontSize: 12, color: "#8892a4",
      display: "flex", gap: 6, alignItems: "center",
    }}>
      <span style={{ color: "#e8eaf0", fontWeight: 600 }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
